import { FC, useEffect, useMemo } from 'react'
import { MachineryType } from '@/domain/models/machineryType.ts'
import { MaintenanceType } from '@/domain/models/maintenanceType.ts'
import { DataGrid } from '@/presentation/common/components/dataGrid/DataGrid.tsx'
import {
  DataGridApiConfig,
  DataGridColumnDefs,
  DataGridDefaultColDef,
} from '@/presentation/common/components/dataGrid/lib/api.types.ts'
import { Maintainer } from '@/presentation/common/components/maintainer/Maintainer.tsx'
import { MTCellUidV2 } from '@/presentation/common/components/maintainer/table/cells/MTCellUidV2.tsx'
import { numberComparator } from '@/presentation/common/components/table/comparators/numberComparator.ts'
import { stringComparator } from '@/presentation/common/components/table/comparators/stringComparator.ts'
import { useAlert } from '@/presentation/common/hooks/useAlert.ts'
import { useMQ } from '@/presentation/common/hooks/useMediaQuery.ts'
import { useMaintainerStore } from '@/presentation/common/store/zustand/maintainer.ts'
import { MachineryActions } from '@/presentation/machinery/forms/MachineryActions.tsx'
import { useDownloadMachineryExcel } from '@/presentation/machinery/hooks/useDownloadMachineryExcel.ts'
import { useQueryMachineryForTable } from '@/presentation/machinery/hooks/useQueryMachineryForTable.ts'

const Machinery: FC = () => {
  const { isTabletAndBelow } = useMQ()
  const { reset, setTitle, setSelectionV2, setActionsSlot, search } = useMaintainerStore()
  const { data, query, refresh, isLoading } = useQueryMachineryForTable()

  const defaultColDef: DataGridDefaultColDef = useMemo(() => ({
    sortable  : true,
    resizable : true,
    minWidth  : 100,
  }), [])

  const columnDefs: DataGridColumnDefs[] = useMemo(() => ([
    {
      field                   : 'type',
      headerName              : 'Tipo',
      comparator              : stringComparator,
      formatValue             : ({ value }) => MachineryType.fromString(value).label(),
      headerCheckboxSelection : true,
      checkboxSelection       : true,
    },
    { field: 'code', headerName: 'Código', cellRenderer: MTCellUidV2, comparator: stringComparator },
    { field: 'brand', headerName: 'Marca', comparator: stringComparator },
    { field: 'model', headerName: 'Modelo', comparator: stringComparator },
    { field: 'year', headerName: 'Año', comparator: numberComparator },
    { field: 'serialNumber', headerName: 'Matrícula', comparator: stringComparator },
    {
      field       : 'maintenanceType',
      headerName  : 'Clase de mantenimiento',
      comparator  : stringComparator,
      formatValue : ({ value }) => value ? MaintenanceType.fromString(value).label() : '',
    },
    { field: 'truckVolume', headerName: 'Volumen m³', comparator: numberComparator },
    {
      field        : 'id',
      headerName   : 'ID',
      cellRenderer : MTCellUidV2,
      comparator   : stringComparator,
    },
  ]), [])

  const tableConfig = useMemo(() => ({
    useMobileView : isTabletAndBelow,
    defaultColDef,
    columnDefs,
    rowHeight     : 50,
    rowData       : data?.items || [],
    rowSelection  : 'multiple',
    pagination    : {
      enabled      : true,
      pageSize     : data?.limit || 10,
      currentPage  : data?.page || 1,
      totalItems   : data?.totalItems || 0,
      onPageChange : (page: number, limit: number) => query({
        params: {
          page,
          limit,
          search,
        },
      }),
    },
    onSelectionChange: (_selectedRow, selectedRows) => setSelectionV2(selectedRows),
  } as DataGridApiConfig), [ data, data?.items, data?.limit, data?.page, data?.totalItems, isTabletAndBelow ])

  const actionsComponent = useMemo(() => <MachineryActions onAction={refresh}/>, [ refresh ])

  // actualizar actions slot
  useEffect(() => {
    setActionsSlot(actionsComponent)
  }, [ actionsComponent ])

  // first load
  useEffect(() => {
    reset()
    setTitle('Maquinarias')
    setActionsSlot(actionsComponent)
    query({
      params: {
        page: 1,
      },
    })
  }, [])

  // when search changes update query
  useEffect(() => {
    query({
      params: {
        search,
        page: 1,
      },
    })
  }, [ search ])

  // DOWNLOAD EXCEL
  const {
    submit: downloadFile,
    isLoading: isExcelLoading,
    isError: isExcelError,
    isSuccess: isExcelSuccess,
    message: excelMessage,
    data: excelFile,
    lastUpdate: lastSubmitTimestamp,
  } = useDownloadMachineryExcel()

  const {
    showLoading: showExcelLoading,
    showError: showExcelError,
    closeAlert: closeExcelAlert,
  } = useAlert({ messageApiKey: 'MachineryExcel-Alert' })

  // Mostrar alerta de excel
  useEffect(() => {
    if (isExcelLoading) {
      showExcelLoading(excelMessage)
    }
    else if (isExcelError) {
      showExcelError(excelMessage)
    }
    else {
      closeExcelAlert()
    }
  }, [ isExcelLoading, isExcelError, isExcelSuccess, excelMessage ])

  // Abrir excel cuando se descargue
  useEffect(() => {
    if (!isExcelLoading && excelFile) {
      window.open(URL.createObjectURL(excelFile))
    }
  }, [ lastSubmitTimestamp ])

  return (
    <Maintainer showExcelDownloadButton onExcelDownloadButtonClick={downloadFile}>
      <DataGrid config={tableConfig} isLoading={isLoading}/>
    </Maintainer>
  )
}

export default Machinery
